import type { ProviderEmailAddress } from './../data/models/email-address.model';
import validationUtils from './validationUtils';
import { BaseEntityUtils } from './BaseEntityUtils';
import emailUtils, { ProviderEmailUse } from './emailUtils';
import phoneUtils, { ProviderPhoneUse } from './phoneUtils';
import addressUtils, { ProviderAddressUse } from './addressUtils';
import zipCodeUtils from './zipCodeUtils';
import insuranceUtils, {
  InsuranceCoverageType,
  InsuranceLimitType,
  InsuranceType,
} from './insuranceUtils';
import type {
  Provider,
  ProviderOptimized,
  Tier3Provider,
} from '../data/models/provider.model';
import type { ProviderPhoneNumber } from '../data/models/phone-number.model';
import type { CoverageArea } from '../data/models/coverage-area.model';
import type { ProviderAddress } from '../data/models/address.model';
import type { Insurance } from '../data/models/insurance.model';
import { AccountingState } from '../data/models/pricing-model.model';
import type { PricingFunction } from '../data/models/pricing-function.model';
import { TransportationType } from '../data/models/transportation-type.model';
import { DateTime } from 'luxon';

class ProviderUtils extends BaseEntityUtils {
  private getObfuscatedValue(val?: string | null): string | null | undefined {
    if (val?.startsWith('xx')) {
      return undefined;
    } else if (val?.length === 0 || val === undefined) {
      return null;
    }
    return val;
  }

  public getDefaults(
    provider: Provider | null | undefined,
    onboardingProviderName?: string,
    legalEntityNATypeID?: number,
    legalEntityLLCTypeID?: number,
  ): any {
    const dispatchEmail = emailUtils.getProviderEmail(
      provider?.emailAddresses,
      ProviderEmailUse.Dispatch,
    );
    const billingEmail = emailUtils.getProviderEmail(
      provider?.emailAddresses,
      ProviderEmailUse.Billing,
    );
    const ownerEmail = emailUtils.getProviderEmail(
      provider?.emailAddresses,
      ProviderEmailUse.Owner,
    );
    const officeAddress = addressUtils.getProviderAddress(
      provider?.addresses,
      ProviderAddressUse.Office,
    );
    const billingAddress = addressUtils.getProviderAddress(
      provider?.addresses,
      ProviderAddressUse.Billing,
    );
    const physicalAddress = addressUtils.getProviderAddress(
      provider?.addresses,
      ProviderAddressUse.Physical,
    );
    const mainPhone = phoneUtils.getProviderPhone(
      provider?.phoneNumbers,
      ProviderPhoneUse.Main,
    );
    const dispatchPhone = phoneUtils.getProviderPhone(
      provider?.phoneNumbers,
      ProviderPhoneUse.Dispatch,
    );
    const billingPhone = phoneUtils.getProviderPhone(
      provider?.phoneNumbers,
      ProviderPhoneUse.Billing,
    );
    const ownerPhone = phoneUtils.getProviderPhone(
      provider?.phoneNumbers,
      ProviderPhoneUse.Owner,
    );
    const commercialInsurance = insuranceUtils.getInsurance(
      provider?.insurancePolicies,
      InsuranceType.General,
    );
    const commercialLimit = insuranceUtils.getInsuranceLimit(
      commercialInsurance,
      InsuranceCoverageType.AggregateLimit,
      InsuranceLimitType.Aggregate,
    );
    const autoInsurance = insuranceUtils.getInsurance(
      provider?.insurancePolicies,
      InsuranceType.Auto,
    );
    const autoBodilyPerPerson = insuranceUtils.getInsuranceLimit(
      autoInsurance,
      InsuranceCoverageType.SplitLimits,
      InsuranceLimitType.BodilyInjuryPerPerson,
    );
    const autoBodilyPerAccident = insuranceUtils.getInsuranceLimit(
      autoInsurance,
      InsuranceCoverageType.SplitLimits,
      InsuranceLimitType.BodilyInjuryPerAccident,
    );
    const autoPropertyDamage = insuranceUtils.getInsuranceLimit(
      autoInsurance,
      InsuranceCoverageType.SplitLimits,
      InsuranceLimitType.PropertyDamagePerAccident,
    );
    const autoCombined = insuranceUtils.getInsuranceLimit(
      autoInsurance,
      InsuranceCoverageType.CombinedSingleLimit,
      InsuranceLimitType.Combined,
    );
    return {
      TransportationProviderName:
        this.getDefaultString(provider?.transportationProviderName) ||
        onboardingProviderName,
      EmailAddress: this.getDefaultString(dispatchEmail?.emailAddress),
      BillingEmailAddress: this.getDefaultString(billingEmail?.emailAddress),
      OwnerFirstName: this.getDefaultString(provider?.ownerFirstName),
      OwnerLastName: this.getDefaultString(provider?.ownerLastName),
      EmployerIdentificationNumber: this.getDefaultString(
        provider?.employerIdentificationNumber,
      ),

      LegalEntityStateCode: this.getDefaultString(
        provider?.legalEntityStateCode,
      ),
      BusinessPermitExpiresOn: this.getDefaultDate(
        provider?.businessPermitExpiresOn,
      ),
      HasUnlimitedTrips: this.getDefaultBoolean(provider?.hasUnlimitedTrips),
      IsClearToTransport: this.getDefaultBoolean(provider?.isClearToTransport),
      IsReadyClearToTransport: this.getDefaultBoolean(
        provider?.isReadyClearToTransport,
      ),
      IsActive: provider
        ? provider.transportationProviderState === 'active'
        : true,

      TransportationTypeID: this.getDefaultNumber(
        provider?.transportationTypeID,
      ),
      DefaultDriverMessagingMethodID: this.getDefaultNumber(
        provider?.defaultDriverMessagingMethodID,
      ),
      MessagingMethodID: this.getDefaultNumber(provider?.messagingMethodID),
      NPINumber: this.getDefaultString(provider?.npiNumber),
      LegalEntityStatusID: this.getDefaultNumber(
        provider?.legalEntityStatusID,
        legalEntityLLCTypeID,
      ),
      LegalEntityTypeID: this.getDefaultNumber(
        provider?.legalEntityTypeID,
        legalEntityNATypeID,
      ),
      IsInternal: this.getDefaultBoolean(provider?.isInternal),
      AddressLine1: this.getDefaultString(officeAddress?.addressLine1),
      AddressLine2: this.getDefaultString(officeAddress?.addressLine2),
      City: this.getDefaultString(officeAddress?.city),
      State: this.getDefaultString(officeAddress?.stateCode),
      ZipCode: zipCodeUtils.formatZipCode(officeAddress?.zipCode),
      County: this.getDefaultString(officeAddress?.county),
      BillingContactFirstName: this.getDefaultString(
        billingAddress?.contactFirstName,
      ),
      BillingContactLastName: this.getDefaultString(
        billingAddress?.contactLastName,
      ),
      BillingAddressLine1: this.getDefaultString(billingAddress?.addressLine1),
      BillingAddressLine2: this.getDefaultString(billingAddress?.addressLine2),
      BillingCity: this.getDefaultString(billingAddress?.city),
      BillingState: this.getDefaultString(billingAddress?.stateCode),
      BillingZipCode: zipCodeUtils.formatZipCode(billingAddress?.zipCode),
      PhysicalAddressLine1: this.getDefaultString(
        physicalAddress?.addressLine1,
      ),
      PhysicalAddressLine2: this.getDefaultString(
        physicalAddress?.addressLine2,
      ),
      PhysicalCity: this.getDefaultString(physicalAddress?.city),
      PhysicalState: this.getDefaultString(physicalAddress?.stateCode),
      PhysicalZipCode: zipCodeUtils.formatZipCode(physicalAddress?.zipCode),
      MainPhone: mainPhone?.phoneNumber,
      MainPhoneExtension: this.getDefaultString(mainPhone?.phoneExtension),
      MainHasSMSConsent:
        provider?.phoneNumbers?.find(
          (phone) =>
            phone.transportationProviderPhoneUseID === ProviderPhoneUse.Main,
        )?.hasSMSConsent || false,
      MainContactFirstName: this.getDefaultString(
        dispatchEmail?.contactFirstName,
      ),
      MainContactLastName: this.getDefaultString(
        dispatchEmail?.contactLastName,
      ),
      HasSMSConsent: this.getDefaultBoolean(mainPhone?.hasSMSConsent),
      DispatchPhone: dispatchPhone?.phoneNumber,
      DispatchPhoneExtension: this.getDefaultString(
        dispatchPhone?.phoneExtension,
      ),
      DispatchHasSMSConsent: true,
      BillingPhone: billingPhone?.phoneNumber,
      BillingPhoneExtension: this.getDefaultString(
        billingPhone?.phoneExtension,
      ),
      BillingHasSMSConsent: true,
      OwnerEmailAddress: this.getDefaultString(ownerEmail?.emailAddress),
      OwnerPhone: this.getDefaultString(ownerPhone?.phoneNumber),
      OwnerPhoneExtension: this.getDefaultString(ownerPhone?.phoneExtension),
      OwnerHasSMSConsent: true,
      CommercialInsuranceCompanyName: this.getDefaultString(
        commercialInsurance?.insuranceCompanyName,
      ),
      CommercialInsurancePolicyNumber: this.getDefaultString(
        commercialInsurance?.insurancePolicyNumber,
      ),
      CommercialInsuranceExpiresOn: this.getDefaultDate(
        commercialInsurance?.insuranceExpiresOn,
      ),
      CommercialInsuranceStrengthID: this.getDefaultNumber(
        commercialInsurance?.insuranceStrengthID,
      ),
      CommercialAggregateAmount: this.getDefaultNumber(
        commercialLimit?.insuredAmount,
      ),
      AutoInsuranceCoverageTypeID: autoCombined?.isActive
        ? InsuranceCoverageType.CombinedSingleLimit
        : InsuranceCoverageType.SplitLimits,
      AutoInsuranceCompanyName: this.getDefaultString(
        autoInsurance?.insuranceCompanyName,
      ),
      AutoInsurancePolicyNumber: this.getDefaultString(
        autoInsurance?.insurancePolicyNumber,
      ),
      AutoInsuranceExpiresOn: this.getDefaultDate(
        autoInsurance?.insuranceExpiresOn,
      ),
      AutoInsuranceStrengthID: this.getDefaultNumber(
        autoInsurance?.insuranceStrengthID,
      ),
      AutoBodilyInjuryPerson: this.getDefaultNumber(
        autoBodilyPerPerson?.insuredAmount,
      ),
      AutoBodilyInjuryAccident: this.getDefaultNumber(
        autoBodilyPerAccident?.insuredAmount,
      ),
      AutoPropertyDamage: this.getDefaultNumber(
        autoPropertyDamage?.insuredAmount,
      ),
      AutoCombinedLimit: this.getDefaultNumber(autoCombined?.insuredAmount),
      BankName: this.getDefaultString(provider?.bankName),
      BankRoutingNumber: this.getDefaultString(provider?.bankRoutingNumber),
      BankAccountNumber: this.getDefaultString(provider?.bankAccountNumber),
      AccountNumber: this.getDefaultString(provider?.accountNumber),
    };
  }

  public getTier3Defaults(provider: Tier3Provider | null | undefined) {
    // const phone = provider ? provider.contactPhoneNumber?.substring(2) : '';
    const phone = provider?.phoneNumbers?.length
      ? provider.phoneNumbers[0]
      : null;
    const email = provider?.emailAddresses?.length
      ? provider.emailAddresses[0]
      : null;
    const country = 1;
    const tripModeIDs = (provider?.vehicleTripModeCapacities || []).map(
      (tm) => tm.tripModeID,
    );
    const coverageAreaIDs = provider?.transportationProviderCoverageAreas
      ? provider.transportationProviderCoverageAreas.map(
          (tpca) => tpca.coverageAreaID,
        )
      : (provider?.coverageAreas || []).map((ca) => ca.coverageAreaID);
    const paymentTypeIDS = (provider?.paymentTypes || []).map(
      (pt) => pt.paymentTypeID,
    );
    return {
      Name: provider?.transportationProviderName || '',
      ContactFirstName: phone?.contactFirstName || '',
      ContactLastName: phone?.contactLastName || '',
      ContactEmail: email?.emailAddress || '',
      ContactPhone: phone?.phoneNumber,
      ContactPhoneExtension: phone?.phoneExtension || '',
      City: provider?.legalEntityCity || '',
      State: provider?.legalEntityStateCode || '',
      TripModes: tripModeIDs,
      CoverageAreas: coverageAreaIDs,
      PaymentMethods: paymentTypeIDS,
      IsClearToTransport: provider?.isClearToTransport || false,
      Capacity: provider?.vehicleTripModeCapacities?.[0]?.capacity || 0,
      IsActive: provider
        ? provider.transportationProviderState === 'active'
        : true,
      IsInternal: provider?.isInternal || false,
    };
  }

  private getCoverageAreas(
    providerId: number | undefined,
    names: string,
    oldCoverageAreas: CoverageArea[],
  ): CoverageArea[] {
    // TODO: put this back when we figure out coverage areas
    // const coverageAreas: CoverageArea[] = names
    //   .split('\n')
    //   .map((area, idx) => {
    //     const id =
    //       idx < oldCoverageAreas.length
    //         ? oldCoverageAreas[idx].coverageAreaID
    //         : undefined;
    //     return {
    //       TransportationProviderCoverageAreaID: id,
    //       TransportationProviderID: providerId,
    //       CoverageAreaName: area,
    //       IsActive: true,
    //     };
    //   })
    //   .filter((a) => a.CoverageAreaName.length > 0);

    // const diff = oldCoverageAreas.length - coverageAreas.length;
    // for (let i = 0; i < diff; i++) {
    //   const current = oldCoverageAreas.length - diff + i;
    //   coverageAreas.push({ ...oldCoverageAreas[current], coverageAreaState: false });
    // }

    const coverageAreas: CoverageArea[] = [];
    return coverageAreas;
  }

  public getProviderFromFormData(
    isTestUser: boolean,
    id: number | undefined,
    formData: any,
    emails: ProviderEmailAddress[] | undefined,
    phoneNumbers: ProviderPhoneNumber[] | undefined,
    addresses: ProviderAddress[] | undefined,
    insurancePolicies: Insurance[] | undefined,
    oldCoverageAreas: CoverageArea[] | undefined,
    isInternal: boolean | undefined,
    transportationTypes: TransportationType[],
  ): Provider {
    const mainEmail = emailUtils.getProviderEmail(
      emails,
      ProviderEmailUse.Dispatch,
    );
    const billingEmail = emailUtils.getProviderEmail(
      emails,
      ProviderEmailUse.Billing,
    );
    const ownerEmail = emailUtils.getProviderEmail(
      emails,
      ProviderEmailUse.Owner,
    );
    const mainPhone = phoneUtils.getProviderPhone(
      phoneNumbers,
      ProviderPhoneUse.Main,
    );
    const dispatchPhone = phoneUtils.getProviderPhone(
      phoneNumbers,
      ProviderPhoneUse.Dispatch,
    );
    const billingPhone = phoneUtils.getProviderPhone(
      phoneNumbers,
      ProviderPhoneUse.Billing,
    );
    const ownerPhone = phoneUtils.getProviderPhone(
      phoneNumbers,
      ProviderPhoneUse.Owner,
    );
    const officeAddress = addressUtils.getProviderAddress(
      addresses,
      ProviderAddressUse.Office,
    );
    const billingAddress = addressUtils.getProviderAddress(
      addresses,
      ProviderAddressUse.Billing,
    );
    const physicalAddress = addressUtils.getProviderAddress(
      addresses,
      ProviderAddressUse.Physical,
    );
    const commercialInsurance = insuranceUtils.getInsurance(
      insurancePolicies,
      InsuranceType.General,
    );
    const autoInsurance = insuranceUtils.getInsurance(
      insurancePolicies,
      InsuranceType.Auto,
    );
    const isSplitLimits =
      formData.AutoInsuranceCoverageTypeID ===
      InsuranceCoverageType.SplitLimits;
    const coverageAreas = formData.CoverageAreas
      ? this.getCoverageAreas(
          id,
          formData.CoverageAreas as string,
          oldCoverageAreas || [],
        )
      : undefined;
    const bpExpire = DateTime.fromISO(formData.BusinessPermitExpiresOn);

    const phoneNumberOwner = formData.OwnerPhone
      ? {
          transportationProviderPhoneID:
            ownerPhone?.transportationProviderPhoneID,
          transportationProviderID: id,
          transportationProviderPhoneUseID: ProviderPhoneUse.Owner,
          contactFirstName: formData.OwnerFirstName,
          contactLastName: formData.OwnerLastName,
          phoneNumberTypeID: 1,
          phoneNumber: formData.OwnerPhone?.replace(/\s/g, '') || null,
          phoneExtension:
            formData.OwnerPhoneExtension?.replace(/\D/g, '') || null,
          transportationProviderPhoneState: formData.IsActive
            ? 'active'
            : 'inactive',
          isPrimaryPhoneNumber: true,
          hasSMSConsent: true,
        }
      : null;

    const rideShareID = transportationTypes.find(
      (tt) => tt.transportationTypeBC === 'RIDESHARE',
    )?.transportationTypeID;
    const tierID = formData.TransportationTypeID === rideShareID ? 2 : 1;
    const p: Provider = {
      transportationProviderID: id,
      transportationProviderTierID: tierID,
      transportationProviderName: formData.TransportationProviderName,
      ownerFirstName: formData.OwnerFirstName || null,
      ownerLastName: formData.OwnerLastName || null,
      employerIdentificationNumber: formData.EmployerIdentificationNumber,
      legalEntityStateCode: formData.LegalEntityStateCode || null,
      businessPermitExpiresOn: bpExpire.isValid
        ? DateTime.now()
            .setZone('utc')
            .toFormat(validationUtils.validDateFormat())
        : null,
      hasUnlimitedTrips: formData.HasUnlimitedTrips,
      isClearToTransport: formData.IsClearToTransport,
      isReadyClearToTransport: formData.IsReadyClearToTransport,
      transportationProviderState: formData.IsActive ? 'active' : 'inactive',
      transportationTypeID: formData.TransportationTypeID || null,
      defaultDriverMessagingMethodID: formData.DefaultDriverMessagingMethodID,
      messagingMethodID: formData.MessagingMethodID,
      npiNumber: formData.NPINumber || null,
      legalEntityStatusID: formData.LegalEntityStatusID || null,
      legalEntityTypeID: formData.LegalEntityTypeID || null,

      isInternal: formData.IsInternal,
      emailAddresses: [
        {
          transportationProviderEmailID:
            mainEmail?.transportationProviderEmailID,
          transportationProviderEmailUseID: ProviderEmailUse.Dispatch,
          emailAddress: formData.EmailAddress,
          contactFirstName: formData.MainContactFirstName,
          contactLastName: formData.MainContactLastName,
          transportationProviderEmailState: 'active',
        },
        {
          transportationProviderEmailID:
            billingEmail?.transportationProviderEmailID,
          transportationProviderEmailUseID: ProviderEmailUse.Billing,
          emailAddress: formData.BillingEmailAddress,
          contactFirstName: formData.BillingContactFirstName,
          contactLastName: formData.BillingContactLastName,
          transportationProviderEmailState: 'active',
        },
        {
          transportationProviderEmailID:
            ownerEmail?.transportationProviderEmailID,
          transportationProviderEmailUseID: ProviderEmailUse.Owner,
          emailAddress: formData.OwnerEmailAddress,
          contactFirstName: formData.OwnerFirstName,
          contactLastName: formData.OwnerLastName,
          transportationProviderEmailState: 'active',
        },
      ],
      phoneNumbers: [
        {
          transportationProviderPhoneID:
            mainPhone?.transportationProviderPhoneID,
          transportationProviderID: id,
          transportationProviderPhoneUseID: ProviderPhoneUse.Main,
          contactFirstName: formData.MainContactFirstName,
          contactLastName: formData.MainContactLastName,
          phoneNumberTypeID: 1,
          phoneNumber: formData.MainPhone?.replace(/\s/g, '') || null,
          phoneExtension:
            formData.MainPhoneExtension?.replace(/\D/g, '') || null,
          transportationProviderPhoneState: formData.IsActive
            ? 'active'
            : 'inactive',
          isPrimaryPhoneNumber: true,
          hasSMSConsent: formData.HasSMSConsent,
        },
        {
          transportationProviderPhoneID:
            dispatchPhone?.transportationProviderPhoneID,
          transportationProviderID: id,
          transportationProviderPhoneUseID: ProviderPhoneUse.Dispatch,
          contactFirstName: formData.MainContactFirstName,
          contactLastName: formData.MainContactLastName,
          phoneNumberTypeID: 1,
          phoneNumber: formData.DispatchPhone?.replace(/\s/g, '') || null,
          phoneExtension:
            formData.DispatchPhoneExtension?.replace(/\D/g, '') || null,
          transportationProviderPhoneState: formData.IsActive
            ? 'active'
            : 'inactive',
          isPrimaryPhoneNumber: true,
          hasSMSConsent: true,
        },
        {
          transportationProviderPhoneID:
            billingPhone?.transportationProviderPhoneID,
          transportationProviderID: id,
          transportationProviderPhoneUseID: ProviderPhoneUse.Billing,
          contactFirstName: formData.BillingContactFirstName,
          contactLastName: formData.BillingContactLastName,
          phoneNumberTypeID: 1,
          phoneNumber: formData.BillingPhone?.replace(/\s/g, '') || null,
          phoneExtension:
            formData.BillingPhoneExtension?.replace(/\D/g, '') || null,
          transportationProviderPhoneState: 'active',
          isPrimaryPhoneNumber: true,
          hasSMSConsent: true,
        },
      ],
      addresses: [
        {
          transportationProviderAddressID:
            officeAddress?.transportationProviderAddressID,
          transportationProviderID: id,
          transportationProviderAddressUseID: ProviderAddressUse.Office,
          contactFirstName: formData.MainContactFirstName,
          contactLastName: formData.MainContactLastName,
          addressLine1: formData.AddressLine1,
          addressLine2: formData.AddressLine2,
          city: formData.City,
          stateCode: formData.State,
          zipCode: formData.ZipCode.replace(/\D/g, ''),
          county: formData.County || '', // TODO: this is temporary until the schema is fixed to allow null
          isCustomLatLong: false,
          transportationProviderAddressState: 'active',
        },
        {
          transportationProviderAddressID:
            billingAddress?.transportationProviderAddressID,
          transportationProviderID: id,
          transportationProviderAddressUseID: ProviderAddressUse.Billing,
          contactFirstName: formData.BillingContactFirstName,
          contactLastName: formData.BillingContactLastName,
          addressLine1: formData.BillingAddressLine1,
          addressLine2: formData.BillingAddressLine2,
          city: formData.BillingCity,
          stateCode: formData.BillingState,
          zipCode: formData.BillingZipCode.replace(/\D/g, ''),
          county: '', // TODO: this is temporary until the schema is fixed to allow null
          isCustomLatLong: false,
          transportationProviderAddressState: 'active',
        },
        {
          transportationProviderAddressID:
            physicalAddress?.transportationProviderAddressID,
          transportationProviderID: id,
          transportationProviderAddressUseID: ProviderAddressUse.Physical,
          contactFirstName: formData.OwnerFirstName,
          contactLastName: formData.OwnerLastName,
          addressLine1: formData.PhysicalAddressLine1,
          addressLine2: formData.PhysicalAddressLine2,
          city: formData.PhysicalCity,
          stateCode: formData.PhysicalState,
          zipCode: formData.PhysicalZipCode.replace(/\D/g, ''),
          county: '', // TODO: this is temporary until the schema is fixed to allow null
          isCustomLatLong: false,
          transportationProviderAddressState: 'active',
        },
      ],
      insurancePolicies: [
        {
          transportationProviderInsuranceID:
            commercialInsurance?.transportationProviderInsuranceID,
          transportationProviderID: id,
          insuranceTypeID: InsuranceType.General,
          insuranceCompanyName: formData.CommercialInsuranceCompanyName,
          insurancePolicyNumber: formData.CommercialInsurancePolicyNumber,
          insuranceExpiresOn: DateTime.fromISO(
            formData.CommercialInsuranceExpiresOn,
          )
            .setZone('utc')
            .toFormat(validationUtils.validDateFormat()),
          insuranceStrengthID: formData.CommercialInsuranceStrengthID,
          insuranceLimits: [
            {
              insuranceCoverageTypeID: InsuranceCoverageType.AggregateLimit,
              insuranceLimitTypeID: InsuranceLimitType.Aggregate,
              insuredAmount: formData.CommercialAggregateAmount || 0,
              isActive: true,
            },
          ],
          insuranceState: 'active', // TODO: this was added pending schema changes
        },
        {
          transportationProviderInsuranceID:
            autoInsurance?.transportationProviderInsuranceID,
          transportationProviderID: id,
          insuranceTypeID: InsuranceType.Auto,
          insuranceCompanyName: formData.AutoInsuranceCompanyName,
          insurancePolicyNumber: formData.AutoInsurancePolicyNumber,
          insuranceExpiresOn: DateTime.fromISO(formData.AutoInsuranceExpiresOn)
            .setZone('utc')
            .toFormat(validationUtils.validDateFormat()),
          insuranceStrengthID: formData.AutoInsuranceStrengthID,
          insuranceLimits: [
            {
              insuranceCoverageTypeID: InsuranceCoverageType.SplitLimits,
              insuranceLimitTypeID: InsuranceLimitType.BodilyInjuryPerPerson,
              insuredAmount: formData.AutoBodilyInjuryPerson || 0,
              isActive: isSplitLimits,
            },
            {
              insuranceCoverageTypeID: InsuranceCoverageType.SplitLimits,
              insuranceLimitTypeID: InsuranceLimitType.BodilyInjuryPerAccident,
              insuredAmount: formData.AutoBodilyInjuryAccident || 0,
              isActive: isSplitLimits,
            },
            {
              insuranceCoverageTypeID: InsuranceCoverageType.SplitLimits,
              insuranceLimitTypeID:
                InsuranceLimitType.PropertyDamagePerAccident,
              insuredAmount: formData.AutoPropertyDamage || 0,
              isActive: isSplitLimits,
            },
            {
              insuranceCoverageTypeID:
                InsuranceCoverageType.CombinedSingleLimit,
              insuranceLimitTypeID: InsuranceLimitType.Combined,
              insuredAmount: formData.AutoCombinedLimit || 0,
              isActive: !isSplitLimits,
            },
          ],
          insuranceState: 'active', // TODO: this was added pending schema changes
        },
      ],
      coverageAreas: coverageAreas,
      bankName: formData.BankName || null,
      accountNumber: formData.AccountNumber || null,
    };

    if (phoneNumberOwner) {
      p.phoneNumbers!.push(phoneNumberOwner);
    }

    const bankRoutingNumber = this.getObfuscatedValue(
      formData.BankRoutingNumber,
    );
    if (bankRoutingNumber === null) {
      p.bankRoutingNumber = null;
    } else if (bankRoutingNumber !== undefined) {
      p.bankRoutingNumber = bankRoutingNumber;
    }
    const bankAccountNumber = this.getObfuscatedValue(
      formData.BankAccountNumber,
    );
    if (bankAccountNumber === null) {
      p.bankAccountNumber = null;
    } else if (bankAccountNumber !== undefined) {
      p.bankAccountNumber = bankAccountNumber || null;
    }

    return p;
  }

  public getTier3ProviderFromData(
    isTestUser: boolean,
    id: number | undefined,
    formData: any,
    allCoverageAreas: CoverageArea[],
    transportationTypeID: number,
    isInternal: boolean | undefined,
  ): Tier3Provider {
    const coverageAreaIDs: number[] = formData.CoverageAreas;
    const coverageAreas = allCoverageAreas.filter((ca) =>
      coverageAreaIDs.includes(ca.coverageAreaID!),
    );
    const provider: Tier3Provider = {
      transportationProviderID: id,
      transportationTypeID,
      transportationProviderTierID: 3,
      transportationProviderName: formData.Name,
      contactFirstName: formData.ContactFirstName,
      contactLastName: formData.ContactLastName,
      contactEmail: formData.ContactEmail,
      contactPhoneNumber: formData.ContactPhone.replace(/\s/g, ''),
      contactPhoneExtension: formData.ContactPhoneExtension || undefined,
      legalEntityCity: formData.City,
      legalEntityStateCode: formData.State,
      transportationProviderState: formData.IsActive ? 'active' : 'inactive',
      tripModeIDs: formData.TripModes,
      coverageAreas,
      paymentTypeIDs: formData.PaymentMethods,
      contactEmailUseBC: 'OWNER',
      contactPhoneUseBC: 'OWNER',
      capacity: Number(formData.Capacity),
      isClearToTransport: formData.IsClearToTransport,
      isInternal: formData.IsInternal,
    };
    return provider;
  }

  public getStateLabel(state: AccountingState): string {
    switch (state) {
      case AccountingState.Active:
        return 'common.active';
      case AccountingState.Inactive:
        return 'common.inactive';
      case AccountingState.Draft:
        return 'common.draft';
      case AccountingState.Deleted:
        return 'common.deleted';
      default:
        return '';
    }
  }

  public convertToOptimized(provider: Provider): ProviderOptimized {
    const optimized: ProviderOptimized = {
      transportationProviderID: provider.transportationProviderID!,
      transportationProviderName: provider.transportationProviderName,
      transportationProviderTierID: provider.transportationProviderTierID!,
      transportationProviderState: provider.transportationProviderState,
      primaryMainOfficePhoneIDs: '', // we don't display this so not necessary to convert
      dispatchPhoneIDs: '', // we don't display this so not necessary to convert
      transportationProviderCoverageAreaIDs: '', // we don't display this so not necessary to convert
      capacityOfTripsToServiceInDay: null, // we don't display this so not necessary to convert
      hasUnlimitedTrips: !!provider.hasUnlimitedTrips,
      isClearToTransport: provider.isClearToTransport,
      isInternal: provider.isInternal,
      transportationTypeBC: '', // we don't display this so not necessary to convert
      transportationProviderTierName: 'TODO: fixme', // TODO: figure out how to get this from provider
      transportationProviderTierBC: '', // we don't display this so not necessary to convert
      transportationTypeName: '', // we don't display this so not necessary to convert
      transportationTypeID: provider.transportationTypeID!,
      dispatchPhones: provider.phoneNumbers || [], // TODO: filter out non-dispatch phones
      primaryMainOfficePhones: provider.phoneNumbers || [], // TODO: filter out non-main phones
      dispatchEmailAddresses:
        provider.emailAddresses?.map((e) => e.emailAddress) || [],
      transportationProviderCoverageAreas:
        provider.transportationProviderCoverageAreas || [],
    };
    return optimized;
  }

  public getPricingFunctionName(
    pricingFunctions: PricingFunction[],
    pricingFunctionBC: string,
  ) {
    const pricingFunction = pricingFunctions.find(
      (pf) => pf.pricingFunctionBC === pricingFunctionBC,
    );
    return pricingFunction?.name || '';
  }
}

const providerUtils = new ProviderUtils();

export default providerUtils;
